import React, { useState, useEffect } from 'react';
import { Bell, BellOff } from 'lucide-react';

const PushNotificationManager = () => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [vapidKey, setVapidKey] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check if push notifications are supported
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setIsSupported(true);
      fetchVapidKey();
      checkSubscriptionStatus();
    }
  }, []);

  const fetchVapidKey = async () => {
    try {
      const response = await fetch('/api/push-notifications/vapid-key');
      const data = await response.json();
      setVapidKey(data.publicKey);
    } catch (error) {
      console.error('Error fetching VAPID key:', error);
    }
  };

  const checkSubscriptionStatus = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (error) {
      console.error('Error checking subscription status:', error);
    }
  };

  const subscribeToPush = async () => {
    if (!isSupported || !vapidKey) {
      alert('Push notifications are not supported or VAPID key is not available');
      return;
    }

    setIsLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });

      // Send subscription to backend
      const response = await fetch('/api/push-notifications/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ subscription })
      });

      if (response.ok) {
        setIsSubscribed(true);
        console.log('Successfully subscribed to push notifications');
      } else {
        throw new Error('Failed to subscribe');
      }
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      alert('Failed to subscribe to push notifications');
    } finally {
      setIsLoading(false);
    }
  };

  const unsubscribeFromPush = async () => {
    setIsLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }

      // Notify backend
      const response = await fetch('/api/push-notifications/unsubscribe', {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        setIsSubscribed(false);
        console.log('Successfully unsubscribed from push notifications');
      }
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      alert('Failed to unsubscribe from push notifications');
    } finally {
      setIsLoading(false);
    }
  };

  const sendTestNotification = async () => {
    try {
      const response = await fetch('/api/push-notifications/test', {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        console.log('Test notification sent');
      } else {
        throw new Error('Failed to send test notification');
      }
    } catch (error) {
      console.error('Error sending test notification:', error);
      alert('Failed to send test notification');
    }
  };

  // Convert VAPID key to Uint8Array
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  if (!isSupported) {
    return (
      <div style={{ 
        padding: '12px', 
        backgroundColor: '#FEF3C7', 
        border: '1px solid #F59E0B', 
        borderRadius: '8px',
        color: '#92400E'
      }}>
        Push notifications are not supported in this browser
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '16px', 
      backgroundColor: '#F9FAFB', 
      border: '1px solid #E5E7EB', 
      borderRadius: '8px',
      marginBottom: '16px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
        {isSubscribed ? <Bell size={20} color="#10B981" /> : <BellOff size={20} color="#6B7280" />}
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
          Push Notifications
        </h3>
      </div>
      
      <p style={{ margin: '0 0 16px 0', color: '#6B7280', fontSize: '14px' }}>
        {isSubscribed 
          ? 'You will receive push notifications for new campaigns and updates.'
          : 'Subscribe to receive push notifications for campaigns and updates.'
        }
      </p>

      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {!isSubscribed ? (
          <button
            onClick={subscribeToPush}
            disabled={isLoading}
            style={{
              backgroundColor: '#0D7463',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1
            }}
          >
            {isLoading ? 'Subscribing...' : 'Subscribe'}
          </button>
        ) : (
          <>
            <button
              onClick={unsubscribeFromPush}
              disabled={isLoading}
              style={{
                backgroundColor: '#EF4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                opacity: isLoading ? 0.7 : 1
              }}
            >
              {isLoading ? 'Unsubscribing...' : 'Unsubscribe'}
            </button>
            <button
              onClick={sendTestNotification}
              style={{
                backgroundColor: '#3B82F6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Send Test
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PushNotificationManager;




